import {
  format,
  getHours,
  getMinutes,
  getSeconds,
  isValid,
  parse,
  parseISO,
  setHours,
  setMinutes,
  setSeconds,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { isString } from 'lodash-es';

import formatDateAsRange from '../../utils/format_date_as_range';
import type { NonStrictDateRange } from '../calendar/date_picker';
import {
  type CalendarDropdownOption,
  type DateRange,
  formatSelectValue,
  type MatchSelectValue,
  type OptionAndNumber,
  type StrictDateRange,
} from '../calendar/default_options';
import type { TimeFormatOptions } from '../calendar/time_picker/time_picker';

export const parseInput = (
  displayValue: string,
  outputFormat: string,
  dateFormat: string,
  canRange: boolean,
  optionsPresets: CalendarDropdownOption[],
  optionsDropdown: CalendarDropdownOption[],
): string | StrictDateRange | false => {
  // check if the input is a date
  const dateValue = parse(displayValue, dateFormat, new Date());
  const isDate = isValid(dateValue);
  if (isDate && displayValue.length === dateFormat.length) {
    return format(dateValue, outputFormat);
  }

  // check if the input is a date range
  const range = displayValue.split(' | ');
  if (canRange && range.length === 2) {
    const min = parse(range[0], dateFormat, new Date());
    const max = parse(range[1], dateFormat, new Date());
    if (
      isValid(min) &&
      isValid(max) &&
      range[0].length === dateFormat.length &&
      range[1].length === dateFormat.length
    ) {
      const value = {
        min: format(min, outputFormat),
        max: format(max, outputFormat),
      };
      return value;
    }
  }

  // check if the input is a preset
  const preset = optionsPresets.find(
    (o) => o.label.toLowerCase() === displayValue.toLowerCase(),
  );
  if (preset) {
    return preset.value;
  }

  // check if the input is a dropdown option
  let calendarValue;
  optionsDropdown.forEach((o) => {
    const split = displayValue.split(o.label);
    if (split.length === 2) {
      const number = Number(split[0]);
      if (!Number.isNaN(number)) {
        calendarValue = formatSelectValue(o.value, number);
      }
    }
  });
  if (calendarValue) {
    return calendarValue;
  }

  return false;
};

export const getOutputValue = (
  value: NonStrictDateRange | string | undefined,
  outputFormat: string,
  rangeAsString?: boolean,
): NonStrictDateRange | string | undefined => {
  let output = value;

  // single value
  if (isString(value)) {
    const date = parseISO(value);
    if (isValid(date)) {
      output = format(new Date(date), outputFormat);
    } else {
      output = value;
    }
  }

  // range
  if (!isString(value) && value && value.min && value.max) {
    output = { ...value };
    if (output.min && output.max) {
      const min = parseISO(output.min);
      const max = parseISO(output.max);
      if (isValid(min) && isValid(max)) {
        output.min = format(new Date(min), outputFormat);
        output.max = format(new Date(max), outputFormat);
        if (rangeAsString) {
          output = formatDateAsRange(output);
        }
      }
    }
  }

  return output;
};

export const formatCalendarDate = (
  date: string | NonStrictDateRange,
): string | Date | DateRange => {
  // single value
  if (isString(date)) {
    const parsedDate = parseISO(date);
    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }

  // range
  if (!isString(date) && date.min && date.max) {
    const min = Date.parse(date.min);
    const max = Date.parse(date.max);
    if (isValid(min) && isValid(max)) {
      return {
        min: parseISO(date.min),
        max: parseISO(date.max),
      };
    }
  }

  return date;
};

function tryFormat(date: Date, formatString: string): string {
  try {
    return format(date, formatString);
  } catch (_err) {
    return '';
  }
}

export const formatDate = (
  date: string | StrictDateRange,
  dateFormat: string,
): string => {
  // single value
  if (isString(date)) {
    const parsedDate = parseISO(date);
    if (!isValid(parsedDate)) {
      return '';
    }
    return tryFormat(parsedDate, dateFormat);
  }
  // range
  if (date.min && date.max) {
    const parsedMin = parseISO(date.min);
    const parsedMax = parseISO(date.max);
    if (!isValid(parsedMin) || !isValid(parsedMax)) {
      return '';
    }
    const min = tryFormat(parsedMin, dateFormat);
    const max = tryFormat(parsedMax, dateFormat);
    if (min === max) {
      return `${min}`;
    }
    return `${min} | ${max}`;
  }
  return '';
};

export const formatRelativeDate = (
  date: string | DateRange,
  optionsPresets: CalendarDropdownOption[],
  optionsDropdown: CalendarDropdownOption[],
  matchSelectValue: MatchSelectValue,
): string | undefined => {
  let value: string | undefined;
  // single value
  if (isString(date)) {
    value = (optionsPresets.find((option) => option.value === date) || {})
      .label;
  }
  // range
  if (!isString(date) && date.min && date.max) {
    // presets
    value = (
      optionsPresets.find(
        (option) =>
          !isString(option.value) &&
          date.max === option.value.max &&
          date.min === option.value.min,
      ) || {}
    ).label;

    // input + dropdown
    if (!value) {
      let match: OptionAndNumber | null | undefined;
      const selected: CalendarDropdownOption | undefined = optionsDropdown.find(
        (option, i) => {
          if (!isString(option.value)) {
            match =
              matchSelectValue(date.min, option.value.min, i) ??
              matchSelectValue(date.max, option.value.max, i);
          }
          return match !== null;
        },
      );
      if (match && selected) {
        value = `${match.number} ${selected.label}`;
      }
    }
  }

  return value;
};

/**
 * Preset ranges for the jobs widget period filter, which stores absolute dates
 * rather than the relative `now-1d` strings the default presets produce.
 */
export const getPresetDatesRelativeToNow = (): CalendarDropdownOption[] => {
  const now = new Date();
  const nowISO = now.toISOString();
  const range = (min: Date, max: Date): StrictDateRange => ({
    min: min.toISOString(),
    max: max.toISOString(),
  });
  return [
    { label: 'Today', value: { min: nowISO, max: nowISO } },
    {
      label: 'Yesterday',
      value: range(startOfDay(subDays(now, 1)), startOfDay(now)),
    },
    {
      label: 'Last week',
      value: range(subWeeks(startOfWeek(now), 1), subDays(startOfWeek(now), 1)),
    },
    {
      label: 'Last month',
      value: range(
        subMonths(startOfMonth(now), 1),
        subDays(startOfMonth(now), 1),
      ),
    },
    {
      label: 'Last year',
      value: range(subYears(startOfYear(now), 1), subDays(startOfYear(now), 1)),
    },
  ];
};

export const getUserTimeFormat = (): '12h' | '24h' => {
  const timeFormat = new Intl.DateTimeFormat([], {
    hour: 'numeric',
    hour12: true,
  }).resolvedOptions().hour12
    ? '12h'
    : '24h';
  return timeFormat;
};

export const insertDateTime = (
  date: Date,
  hours: number,
  minutes: number,
  seconds: number,
  timeFormat: TimeFormatOptions,
) => {
  let adjustedHours = hours;

  if (timeFormat !== '24h') {
    // When a user selects 12h format, we need to adjust the hours to match the corresponding 12h format.
    if (timeFormat === 'am' && hours >= 12) {
      // If user previously had 24h format and hours are greater than or equal to 12,
      // we have to subtracts 12 from the adjustedHours to convert it to the corresponding 12h format.
      adjustedHours -= 12;
    }
    if (timeFormat === 'pm' && hours < 12) {
      // If user selects pm and hours are less than 12,
      // we have to add 12 to adjustedHours to convert it to the corresponding 12h format.
      adjustedHours += 12;
    }
  }

  const updatedDate = setHours(
    setMinutes(setSeconds(date, seconds), minutes),
    adjustedHours,
  );
  return updatedDate;
};

export const extractDateTimeInfo = (dateString: Date) => {
  const date = new Date(dateString);
  const hours = getHours(date);
  const minutes = getMinutes(date);
  const seconds = getSeconds(date);

  const timeFormat: TimeFormatOptions = hours < 12 ? 'am' : 'pm';

  return { hours, minutes, seconds, timeFormat };
};

export const validateHoursByTimeFormat = (
  hours: string,
  timeFormat: TimeFormatOptions,
) => {
  let hrs = Number(hours);
  if (timeFormat !== '24h') {
    if (hrs === 0) {
      hrs = 12;
      return hrs.toLocaleString(undefined, {
        minimumIntegerDigits: 2,
      });
    }
    if (hrs > 11) {
      hrs = hrs - 12;
      return hrs.toLocaleString(undefined, {
        minimumIntegerDigits: 2,
      });
    }
  }
  return hours;
};
